import {
  AccountResource,
  activeSourceCurrenciesAtom,
  CurrencyExchangeSourceAndTargetCurrencyManager,
  LoadableState,
  sourceCurrenciesAtom,
  SourceCurrencyList,
  targetCurrenciesAtom,
  TargetCurrencyList,
} from '@eih-netbroker/common';
import { createContext, ReactNode, RefObject, useEffect, useRef } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { useResource, useResourceHeaders } from '../../../../shared/services/useResource';
import { store } from '../../../../shared/state/store';
import { getClientNormalAccountId } from '../../../../shared/utils/accountUtils';
import { createLogger } from '../../../../shared/utils/logger';
import { getExchangePeriodHours } from '../../../../shared/utils/useLocalStorage';
import { CashPopoverContent, CashPopoverContentProps } from './CashPopoverContent';

type CurrencyExchangeSourceAndTargetCurrencyManagerProps = {
  currencyExchangeEvents: RefObject<CurrencyExchangeSourceAndTargetCurrencyManager>;
};

export const CurrencyExchangeSourceAndTargetCurrencyManagerContext = createContext<
  CurrencyExchangeSourceAndTargetCurrencyManagerProps | undefined
>(undefined);

/**
 * This wrapper creates and exposes the currencyExchangeEvents instance for managing the source and active currencies
 */
type CurrencyExchangeSourceAndTargetCurrencyManagerProviderProps = {
  children: ReactNode;
};

const logger = createLogger('CurrencyExchangeEvents');
const exchangePeriodHours = getExchangePeriodHours();

export const CurrencyExchangeSourceAndTargetCurrencyManagerProvider = ({
  children,
}: CurrencyExchangeSourceAndTargetCurrencyManagerProviderProps) => {
  const { translation } = useLocale();
  const { selectedClient } = useAccounts();
  const { getResourceHeaderWithToken, getResourceHeaderWithTokenForAccount } = useResourceHeaders();
  const accountResource = useResource(AccountResource);
  const selectedClientNormalAccountId = getClientNormalAccountId(selectedClient);
  const currencyExchangeEvents = useRef(
    CurrencyExchangeSourceAndTargetCurrencyManager.getInstance({
      getSourceCurrencies: (targetCurrency?: string) =>
        accountResource.getSourceCurrencies(
          { targetCcyCode: targetCurrency },
          selectedClientNormalAccountId
            ? getResourceHeaderWithTokenForAccount(selectedClientNormalAccountId)
            : getResourceHeaderWithToken(),
        ),
      getTargetCurrencies: (currency: string) =>
        accountResource.getTargetCurrencies({ sourceCcyCode: currency }, getResourceHeaderWithToken()),
      messages: {
        loadSourceCurrenciesFailed: translation.currencyChange.error.loadSourceCurrenciesFailed,
        loadTargetCurrenciesFailed: translation.currencyChange.error.loadTargetCurrenciesFailed,
      },
      settersGetters: {
        get activeSourceCurrencies() {
          return store.get(activeSourceCurrenciesAtom);
        },
        set activeSourceCurrencies(value: LoadableState<string[]>) {
          store.set(activeSourceCurrenciesAtom, value);
        },
        get sourceCurrencies() {
          return store.get(sourceCurrenciesAtom);
        },
        set sourceCurrencies(value: LoadableState<SourceCurrencyList>) {
          store.set(sourceCurrenciesAtom, value);
        },
        get targetCurrencies() {
          return store.get(targetCurrenciesAtom);
        },
        set targetCurrencies(value: LoadableState<TargetCurrencyList>) {
          store.set(targetCurrenciesAtom, value);
        },
      },
      exchangePeriodHours,
      logger,
    }),
  );

  // Since the currencyExchangeEvents is a non-react instance, we need to update method refs.
  useEffect(() => {
    currencyExchangeEvents.current.updateRefs(
      (targetCurrency?: string) =>
        accountResource.getSourceCurrencies(
          { targetCcyCode: targetCurrency },
          selectedClientNormalAccountId
            ? getResourceHeaderWithTokenForAccount(selectedClientNormalAccountId)
            : getResourceHeaderWithToken(),
        ),
      (currency: string) =>
        accountResource.getTargetCurrencies({ sourceCcyCode: currency }, getResourceHeaderWithToken()),
    );
  }, [
    accountResource,
    getResourceHeaderWithToken,
    getResourceHeaderWithTokenForAccount,
    selectedClientNormalAccountId,
  ]);

  return (
    <CurrencyExchangeSourceAndTargetCurrencyManagerContext.Provider value={{ currencyExchangeEvents }}>
      {children}
    </CurrencyExchangeSourceAndTargetCurrencyManagerContext.Provider>
  );
};

export const StandaloneCurrencyExchangeSourceAndTargetCurrencyManagerProvider = (props: CashPopoverContentProps) => {
  return (
    <CurrencyExchangeSourceAndTargetCurrencyManagerProvider>
      <CashPopoverContent {...props} />
    </CurrencyExchangeSourceAndTargetCurrencyManagerProvider>
  );
};
